import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Check, Phone, ArrowLeft, MapPin, Clock } from "lucide-react";
import Seo from "../components/Seo";
import QuoteWizard from "../components/QuoteWizard";
import { CTA } from "../components/sections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, track } from "../lib/api";
import { OG_IMG } from "../data/site";

export default function SuburbPage() {
  const { slug } = useParams();
  const [sub, setSub] = useState(null);
  const [gone, setGone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSub(null); setGone(false);
    api.get(`/content/suburbs/${slug}`)
      .then((r) => { if (r.data.has_page) setSub(r.data); else setGone(true); })
      .catch(() => setGone(true));
  }, [slug]);
  useEffect(() => { if (gone) navigate("/areas", { replace: true }); }, [gone, navigate]);

  if (!sub) return <div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-brand-navy" /></div>;
  const seo = sub.seo || {};
  const faqLd = (sub.faqs && sub.faqs.length) ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: sub.faqs.map((f) => ({ "@type": "Question", name: f[0], acceptedAnswer: { "@type": "Answer", text: f[1] } })) } : null;

  return (
    <>
      <Seo title={seo.meta_title || `Sell Your Car in ${sub.name} | Car To Cash`} description={seo.meta_description || sub.intro} canonical={`https://cartocash.com.au/areas/${sub.slug}`} image={OG_IMG} jsonLd={faqLd} />
      <section className="noise-dark text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/areas" className="inline-flex items-center gap-1 text-sm font-bold text-white/70 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> All areas</Link>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black leading-tight">Sell your car in {sub.name}</h1>
            <p className="mt-4 text-lg text-white/80 max-w-lg">{sub.intro}</p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm">
              {sub.region && <span className="inline-flex items-center gap-1 text-white/80"><MapPin className="h-4 w-4 text-brand-gold" /> {sub.region}</span>}
              {sub.pickup_window && <span className="inline-flex items-center gap-1 text-white/80"><Clock className="h-4 w-4 text-brand-gold" /> Pickup {sub.pickup_window}</span>}
            </div>
            <ul className="mt-6 space-y-2">
              {["Free, no-obligation valuation", "We come to you in " + sub.name, "Paid on the spot — cash or EFT", "Free removal, driveable or not"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-white/90"><Check className="h-5 w-5 text-brand-gold" strokeWidth={3} /> {t}</li>
              ))}
            </ul>
            <a href="tel:0431206206" onClick={() => track("phone_tap", { where: "suburb" })} className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-gold px-6 py-3.5 font-extrabold text-brand-navy hover:bg-brand-goldDark transition-colors" data-testid="suburb-call-btn"><Phone className="h-4 w-4" /> 0431 206 206</a>
          </div>
          <div><QuoteWizard /></div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-lg leading-relaxed text-brand-navy space-y-5">
        {(sub.body || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        {sub.nearby && sub.nearby.length > 0 && (
          <p className="text-base text-brand-muted"><strong className="text-brand-navy">Nearby suburbs we also service:</strong> {sub.nearby.join(", ")}.</p>
        )}
      </section>

      {sub.faqs && sub.faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 sm:px-6 pb-8" data-testid="suburb-faq">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy">{sub.name} — common questions</h2>
          <Accordion type="single" collapsible className="mt-6 space-y-3">
            {sub.faqs.map((f, i) => (
              <AccordionItem key={f[0]} value={`f${i}`} className="rounded-2xl border border-brand-line bg-white px-5">
                <AccordionTrigger className="text-left font-display font-bold text-brand-navy hover:no-underline">{f[0]}</AccordionTrigger>
                <AccordionContent className="text-brand-muted leading-relaxed">{f[1]}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
      <CTA />
    </>
  );
}
